import React, { useState } from 'react';
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Person, Lock, Eye, EyeSlash, Envelope, BoxArrowInRight } from 'react-bootstrap-icons';
import { adminInstance } from '../../services/axiosInstance';
import { BRAND } from '../../services/appConfig';
import Loader from '../shared/Loader';
import './AppLogin.css';

const AppLogin = () => {
  const { register, handleSubmit, formState: { errors } } = useForm({ mode: "all" });
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const obj = { UserName: userName, Password: password };
      const response = await adminInstance.post('Login', obj);
      const data = decodeJWT(response.data);
      sessionStorage.setItem('loginToken', response.data);
      sessionStorage.setItem('role', data.role);
      sessionStorage.setItem('userrole', data.UserRole);
      sessionStorage.setItem('username', userName);
      setIsLoading(false);
      navigate('/');
    } catch (err) {
      setIsLoading(false);
      setError("Invalid login credentials!");
    }
  };

  const forgetPassword = async () => {
    try {
        if(!email || email.length === 0 ){
            alert("Please enter email address and try again");
            return;
        }

      setIsLoading(true);
      setError(null);
      const obj = { Email: email };
      const response = await adminInstance.post('ForgotPassword', obj);
      alert(`Reset details sent successfully!`);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setError("Invalid Username !");
    }
  };

  const decodeJWT = (token) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(jsonPayload);
  };

  return (
    <>
      <Loader loading={isLoading} message="Logging in..." height="300px" />
      <div className="login-page">
        <div className="login-shell">

          {/* Brand panel - becomes a short banner on phones */}
          <div
            className="login-hero"
            style={{ backgroundImage: `url("${BRAND.loginBgUrl}")` }}
          >
            <span className="login-hero-eyebrow">{BRAND.name}</span>
            <h2>{BRAND.appName}</h2>
            <p>{BRAND.tagline}</p>
          </div>

          {/* Form panel */}
          <div className="login-form-panel">
            <img
              src={BRAND.logoUrl}
              alt={BRAND.name}
              className="login-logo"
            />

            <h1 className="login-title">Welcome back</h1>
            <p className="login-subtitle">Sign in to your account to continue</p>

            <Form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Form.Group className="mb-3" controlId="loginUserName">
                <Form.Label>Username</Form.Label>
                <div className="login-input">
                  <Person className="login-input-icon" size={18} />
                  <Form.Control
                    type="text"
                    placeholder="Enter username"
                    autoComplete="username"
                    autoFocus
                    isInvalid={!!errors.userName}
                    {...register("userName", { required: true })}
                    onChange={(e) => setUserName(e.target.value)}
                  />
                </div>
                {errors.userName && <small className="login-field-error">Username is required</small>}
              </Form.Group>

              <Form.Group className="mb-3" controlId="loginPassword">
                <Form.Label>Password</Form.Label>
                <div className="login-input has-toggle">
                  <Lock className="login-input-icon" size={17} />
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    isInvalid={!!errors.password}
                    {...register("password", { required: true })}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="login-toggle"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <small className="login-field-error">Password is required</small>}
              </Form.Group>

              <div className="login-row">
                <Button
                  variant="link"
                  className="login-link"
                  onClick={() => { setEmail(''); setShowModal(true); }}
                >
                  Forgot password?
                </Button>
              </div>

              {error && <Alert variant="danger" className="py-2">{error}</Alert>}

              <Button type="submit" className="login-submit w-100" disabled={isLoading}>
                {isLoading
                  ? <Spinner animation="border" size="sm" />
                  : <>Sign in <BoxArrowInRight size={18} /></>}
              </Button>
            </Form>

            <div className="login-footer">
              &copy; {new Date().getFullYear()} {BRAND.name}. All rights reserved.
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Forgot Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Enter the email address linked to your account and we'll send you reset details.
          </p>
          <Form onSubmit={(e) => { e.preventDefault(); forgetPassword(); setShowModal(false); }}>
            <Form.Group controlId="forgotEmail">
              <Form.Label>Email address</Form.Label>
              <div className="login-input">
                <Envelope className="login-input-icon" size={16} />
                <Form.Control
                  type="email"
                  placeholder="example@domain.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button className="login-submit px-4" style={{ height: 40 }} onClick={() => {
            forgetPassword();
            setShowModal(false);
          }}>
            Send Reset Link
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AppLogin;
